import { useEffect, useRef } from 'react';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { usePlaybackStore } from '../../store/playbackStore';
import { useCursorStore } from '../../store/cursorStore';
import { lapKey } from '../../store/colours';

/** Format seconds as m:ss.d for the playback clock. */
function clock(t: number): string {
  const mins = Math.floor(t / 60);
  const secs = t - mins * 60;
  return `${mins}:${secs.toFixed(1).padStart(4, '0')}`;
}

/** Nearest sample index for an elapsed time in an ascending time grid. */
function indexAtTime(time: Float64Array, target: number): number {
  let lo = 0;
  let hi = time.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (time[mid] < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

const SPEEDS = [0.25, 0.5, 1, 2];

/** Geometrically-centred playback glyphs (Unicode glyphs have off-centre side
 *  bearings in system-ui, so we draw them ourselves). All use a 16×16 viewBox. */
function PlayIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="1em" height="1em" aria-hidden="true" focusable="false">
      <path d="M5 3.5v9l7.5-4.5z" fill="currentColor" />
    </svg>
  );
}
function PauseIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="1em" height="1em" aria-hidden="true" focusable="false">
      <rect x="4.5" y="3.5" width="2.6" height="9" fill="currentColor" />
      <rect x="8.9" y="3.5" width="2.6" height="9" fill="currentColor" />
    </svg>
  );
}
function ReplayIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="1em" height="1em" aria-hidden="true" focusable="false">
      <path
        d="M8 3.2a4.8 4.8 0 1 1-4.53 3.2"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path d="M8 1l0 4.2L4.4 3.1z" fill="currentColor" />
    </svg>
  );
}
function StopIcon(): React.ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="1em" height="1em" aria-hidden="true" focusable="false">
      <rect x="4" y="4" width="8" height="8" rx="1" fill="currentColor" />
    </svg>
  );
}

/**
 * Realtime playback controls: a play/pause button and a scrubbable timeline that
 * animate every loaded lap along the track map at its own pace (see TrackMap),
 * while the graph cursor follows the reference car's position.
 */
export function PlaybackBar(): React.ReactElement | null {
  const referenceLap = useWorkspaceStore((s) => s.referenceLap);
  const comparisonLaps = useWorkspaceStore((s) => s.comparisonLaps);
  const resampled = useWorkspaceStore((s) => s.resampled);

  const playing = usePlaybackStore((s) => s.playing);
  const elapsed = usePlaybackStore((s) => s.elapsed);
  const duration = usePlaybackStore((s) => s.duration);
  const speed = usePlaybackStore((s) => s.speed);
  const toggle = usePlaybackStore((s) => s.toggle);
  const tick = usePlaybackStore((s) => s.tick);
  const seek = usePlaybackStore((s) => s.seek);
  const setSpeed = usePlaybackStore((s) => s.setSpeed);
  const reset = usePlaybackStore((s) => s.reset);
  const setDuration = usePlaybackStore((s) => s.setDuration);

  const setActive = useCursorStore((s) => s.setActive);

  // Reference lap's time/distance grid, to map elapsed -> distance for the graphs.
  const refGrid = referenceLap ? resampled[lapKey(referenceLap)] : undefined;

  // The playback timeline spans the longest loaded lap. Computed here (not in
  // TrackMap) so playback is available even on tabs/layouts where the map isn't
  // mounted (e.g. the mobile Graphs tab).
  useEffect(() => {
    if (!referenceLap) {
      setDuration(0);
      return;
    }
    let dur = 0;
    for (const ref of [referenceLap, ...comparisonLaps]) {
      const grid = resampled[lapKey(ref)];
      if (!grid) continue;
      const t = grid.time[grid.time.length - 1];
      if (Number.isFinite(t) && t > dur) dur = t;
    }
    setDuration(dur);
  }, [referenceLap, comparisonLaps, resampled, setDuration]);

  // Reset the clock only when the SET of loaded laps actually changes (not when
  // a view re-mounts). This lives here rather than in TrackMap so that switching
  // to the mobile Map tab — which remounts TrackMap — no longer stops playback.
  const lapSigRef = useRef<string>('');
  useEffect(() => {
    const sig = referenceLap
      ? [lapKey(referenceLap), ...comparisonLaps.map(lapKey)].join('|')
      : '';
    if (sig !== lapSigRef.current) {
      lapSigRef.current = sig;
      reset();
    }
  }, [referenceLap, comparisonLaps, reset]);

  // Drive the graph cursor from the playback clock (follows the reference car).
  useEffect(() => {
    if (!refGrid) return;
    if (elapsed <= 0 && !playing) return;
    const idx = indexAtTime(refGrid.time, elapsed);
    setActive(refGrid.distance[idx], 'playback');
  }, [elapsed, playing, refGrid, setActive]);

  // Real-time animation clock.
  const rafRef = useRef(0);
  const lastRef = useRef(0);
  useEffect(() => {
    if (!playing) return;
    lastRef.current = performance.now();
    const step = (now: number): void => {
      const dt = (now - lastRef.current) / 1000;
      lastRef.current = now;
      tick(dt);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, tick]);

  if (!referenceLap || duration <= 0) return null;

  const atEnd = elapsed >= duration;

  return (
    <div className="playback-bar">
      <button
        className="play-btn"
        onClick={toggle}
        title={playing ? 'Pause' : atEnd ? 'Replay' : 'Play'}
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? <PauseIcon /> : atEnd ? <ReplayIcon /> : <PlayIcon />}
      </button>
      <div className="pb-scrub">
        <span className="small muted clock">{clock(elapsed)}</span>
        <input
          className="timeline"
          type="range"
          min={0}
          max={duration}
          step={0.05}
          value={Math.min(elapsed, duration)}
          onChange={(e) => seek(Number(e.target.value))}
        />
        <span className="small muted clock">{clock(duration)}</span>
      </div>
      <select
        className="small pb-speed"
        value={speed}
        onChange={(e) => setSpeed(Number(e.target.value))}
        title="Playback speed"
      >
        {SPEEDS.map((s) => (
          <option key={s} value={s}>
            {s}×
          </option>
        ))}
      </select>
      <button
        className="small stop-btn"
        onClick={() => {
          reset();
          setActive(null);
        }}
        title="Stop and rewind"
        aria-label="Stop and rewind"
      >
        <StopIcon />
      </button>
    </div>
  );
}
